import { RentIndexRequestDto } from '../../../@types/rent-index-types'
import { AppDispatch } from '../../../@types/store'
import { postRentIndex } from '../../../api/rentIndex/rentIndexApi'
import { getDefaultLogger } from '../../../services/logger/logger'
import { InitState } from '../../reducers/root/initialState'
import {
  selectHasValidAddressForValuation,
  selectHasValidConstructionYearForRentIndex,
} from '../../selectors/insertionSelectors'
import {
  rentIndexRequest,
  rentIndexNotAvailable,
  rentIndexError,
  rentIndexReset,
  rentIndexSuccess,
} from '../../reducers/rentIndex/rentIndexFeature'

const RENT_INDEX_DEBOUNCE_TIMEOUT_MS = 1000
let rentIndexTimer: ReturnType<typeof setTimeout>

type OptionalString = string | undefined
type OptionalNumber = number | undefined
type OptionalNumberLike = number | string | undefined

const toOptionalTrimmedString = (value: OptionalString): OptionalString => {
  if (!value) {
    return undefined
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : undefined
}

const toOptionalNumber = (value: OptionalString): OptionalNumber => {
  const trimmedValue = toOptionalTrimmedString(value)
  if (!trimmedValue) {
    return undefined
  }

  const normalizedValue = trimmedValue.replace(',', '.')
  const parsedValue = Number(normalizedValue)
  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

const toOptionalInteger = (value: OptionalNumberLike): OptionalNumber => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined
  }

  const parsedValue = toOptionalNumber(value)
  return parsedValue !== undefined && Number.isInteger(parsedValue) ? parsedValue : undefined
}

const createRequestFingerprint = ({
  zip,
  city,
  street,
  houseNumber,
  livingArea,
  constructionYear,
}: RentIndexRequestDto) => `${zip}|${city}|${street}|${houseNumber}|${livingArea}|${constructionYear}`

const isCurrentRequestFingerprint = (state: InitState, requestFingerprint: string) => {
  const currentRequestPayload = createRentIndexRequest(state)

  return currentRequestPayload !== undefined && createRequestFingerprint(currentRequestPayload) === requestFingerprint
}

const createRentIndexRequest = (state: InitState): RentIndexRequestDto | undefined => {
  const { postcode, city, street, houseNumber, livingSpace, constructionYear, unknownConstructionYear } =
    state.insertionFeature.insertionData

  const zip = toOptionalTrimmedString(postcode)
  const trimmedCity = toOptionalTrimmedString(city)
  const trimmedStreet = toOptionalTrimmedString(street)
  const trimmedHouseNumber = toOptionalTrimmedString(houseNumber)
  const livingArea = toOptionalNumber(livingSpace)
  const readyConstructionYear = unknownConstructionYear ? undefined : toOptionalInteger(constructionYear)

  if (!zip || !trimmedCity || !trimmedStreet || !trimmedHouseNumber || !livingArea || !readyConstructionYear) {
    return undefined
  }

  return {
    zip,
    city: trimmedCity,
    street: trimmedStreet,
    houseNumber: trimmedHouseNumber,
    livingArea,
    constructionYear: readyConstructionYear,
  }
}

export const fetchRentIndexIfReady = () => async (dispatch: AppDispatch, getState: () => InitState) => {
  const state = getState()
  const { rentIndexFeature } = state
  const hasValidInput = selectHasValidAddressForValuation(state) && selectHasValidConstructionYearForRentIndex(state)
  const requestPayload = hasValidInput ? createRentIndexRequest(state) : undefined
  if (!requestPayload) {
    dispatch(rentIndexReset())
    return
  }

  const requestFingerprint = createRequestFingerprint(requestPayload)
  const isUnchangedRequest =
    rentIndexFeature.lastRequestFingerprint === requestFingerprint &&
    ['available', 'notAvailable', 'error'].includes(rentIndexFeature.status)

  if (isUnchangedRequest) {
    return
  }

  dispatch(rentIndexRequest())
  try {
    const rentIndexResult = await postRentIndex(requestPayload)
    if (!isCurrentRequestFingerprint(getState(), requestFingerprint)) {
      return
    }

    if (!rentIndexResult) {
      dispatch(rentIndexReset())
      return
    }

    if (rentIndexResult.status === 'notAvailable') {
      dispatch(rentIndexNotAvailable({ requestFingerprint }))
      return
    }

    if (rentIndexResult.status === 'error') {
      dispatch(rentIndexError({ requestFingerprint }))
      return
    }

    dispatch(rentIndexSuccess({ rentIndexResult: rentIndexResult.rentIndex, requestFingerprint }))
  } catch (error) {
    if (!isCurrentRequestFingerprint(getState(), requestFingerprint)) {
      return
    }

    getDefaultLogger().warn('Failed to fetch rent index', error)
    dispatch(rentIndexError({ requestFingerprint }))
  }
}

export const fetchRentIndexDebounced = () => (dispatch: AppDispatch) => {
  clearTimeout(rentIndexTimer)
  rentIndexTimer = setTimeout(() => {
    dispatch(fetchRentIndexIfReady())
  }, RENT_INDEX_DEBOUNCE_TIMEOUT_MS)
}
